class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}

if (!window.customElements.get('cart-remove-button')) {
  window.customElements.define('cart-remove-button', CartRemoveButton);
}

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  updateQuantity(line, quantity, name, variantId) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        const quantityElement =
          document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');

        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        this.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
        });
        const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
        let message = '';
        if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem =
          document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
        }

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

if (!customElements.get('cart-items')){
    window.customElements.define('cart-items', CartItems);
}

class CartFreeGift extends HTMLElement {
  constructor() {
    super();

    this.syncing = false;
    this.pendingCart = null;
    this.abortController = null;
  }

  connectedCallback() {
    this.abortController = new AbortController();

    document.addEventListener(
      'cart:change',
      (event) => this.queueSync(event.detail?.cart),
      { signal: this.abortController.signal }
    );
    document.addEventListener(
      'cart:refresh',
      () => this.fetchCartAndQueueSync(),
      { signal: this.abortController.signal }
    );

    this.fetchCartAndQueueSync();
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  get enabled() {
    return this.dataset.enabled === 'true';
  }

  get available() {
    return this.dataset.available === 'true';
  }

  get variantId() {
    return Number.parseInt(this.dataset.variantId, 10) || null;
  }

  get threshold() {
    const baseThreshold = Number.parseInt(this.dataset.threshold, 10) || 0;
    return Math.round(baseThreshold * (window.Shopify?.currency?.rate || 1));
  }

  get marker() {
    return this.dataset.marker;
  }

  async fetchCart() {
    const response = await fetch(`${window.Shopify.routes.root}cart.js`, {
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) {
      throw new Error(`Cart request failed (${response.status})`);
    }

    return response.json();
  }

  async fetchCartAndQueueSync() {
    try {
      this.queueSync(await this.fetchCart());
    } catch (error) {
      console.error('Failed to load the cart for free gift synchronization:', error);
    }
  }

  queueSync(cart) {
    if (!cart?.items) return;

    this.pendingCart = cart;
    if (!this.syncing) this.drainSyncQueue();
  }

  async drainSyncQueue() {
    this.syncing = true;

    try {
      while (this.pendingCart) {
        const cart = this.pendingCart;
        this.pendingCart = null;
        await this.synchronize(cart);
      }
    } catch (error) {
      console.error('Failed to synchronize the cart free gift:', error);
    } finally {
      this.syncing = false;

      if (this.pendingCart) this.drainSyncQueue();
    }
  }

  isManagedGift(item) {
    return item.properties?._free_gift === this.marker;
  }

  async synchronize(cart) {
    let currentCart = cart;
    let cartChanged = false;

    try {
      const managedGifts = currentCart.items.filter((item) => this.isManagedGift(item));
      const managedGiftTotal = managedGifts.reduce((total, item) => total + item.final_line_price, 0);
      const qualifyingTotal = Math.max(0, currentCart.total_price - managedGiftTotal);
      const hasQualifyingItem = currentCart.items.some((item) => !this.isManagedGift(item));
      const shouldHaveGift =
        this.enabled &&
        this.available &&
        this.variantId !== null &&
        hasQualifyingItem &&
        qualifyingTotal >= this.threshold;

      const matchingGift = managedGifts.find((item) => item.variant_id === this.variantId);
      const updates = {};

      for (const item of managedGifts) {
        if (!shouldHaveGift || item.key !== matchingGift?.key) {
          updates[item.key] = 0;
        }
      }

      if (shouldHaveGift && matchingGift && matchingGift.quantity !== 1) {
        updates[matchingGift.key] = 1;
      }

      if (Object.keys(updates).length > 0) {
        currentCart = await this.mutateCart('update', { updates });
        cartChanged = true;
      }

      const giftStillMissing =
        shouldHaveGift &&
        !currentCart.items.some(
          (item) => this.isManagedGift(item) && item.variant_id === this.variantId
        );

      if (giftStillMissing) {
        currentCart = await this.mutateCart('add', {
          items: [
            {
              id: this.variantId,
              quantity: 1,
              properties: { _free_gift: this.marker },
            },
          ],
        });
        cartChanged = true;
      }
    } finally {
      if (cartChanged) this.publishCartChange(currentCart);
    }
  }

  getSectionsToRender() {
    const sections = [this.dataset.sectionId];

    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections },
      })
    );

    return [...new Set(sections.filter(Boolean))];
  }

  async mutateCart(action, payload) {
    const sections = this.getSectionsToRender();
    const response = await fetch(`${window.Shopify.routes.root}cart/${action}.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        ...payload,
        sections,
        sections_url: window.location.pathname,
      }),
    });
    const responseJson = await response.json();

    if (!response.ok || responseJson.errors) {
      throw new Error(responseJson.description || responseJson.message || 'Cart mutation failed');
    }

    const updatedCart = await this.fetchCart();
    updatedCart.sections = responseJson.sections || {};
    return updatedCart;
  }

  publishCartChange(cart) {
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', {
        bubbles: true,
        detail: {
          baseEvent: 'free-gift:change',
          cart,
        },
      })
    );
  }
}

if (!customElements.get('cart-free-gift')) {
  window.customElements.define('cart-free-gift', CartFreeGift);
}

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}
